import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'

const DISMISS_KEY = 'pwa-install-dismissed'
const ENGAGEMENT_MS = 30_000

export type PwaPlatform = 'windows' | 'macos' | 'android' | 'ios' | 'other'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function isStandaloneDisplay(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
  )
}

export function isIosDevice(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

/**
 * Best-effort platform detection for install instructions. Windows and macOS need
 * the browser menu (Edge/Chrome never show a share sheet), iOS needs the share
 * sheet, and Android has both paths.
 */
export function detectPwaPlatform(): PwaPlatform {
  const userAgent = navigator.userAgent
  const platform = navigator.platform || ''
  const maxTouchPoints = navigator.maxTouchPoints || 0

  if (/iphone|ipad|ipod/i.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)) {
    return 'ios'
  }
  if (/android/i.test(userAgent)) {
    return 'android'
  }
  if (/win/i.test(platform) || /windows/i.test(userAgent)) {
    return 'windows'
  }
  if (/mac/i.test(platform) || /macintosh/i.test(userAgent)) {
    return 'macos'
  }
  return 'other'
}

/** Locale key describing how to install manually when no install prompt is available. */
export function manualInstallHintKey(platform: PwaPlatform): string {
  if (platform === 'windows') return 'pwa.installManualWindows'
  if (platform === 'macos') return 'pwa.installManualMac'
  if (platform === 'android') return 'pwa.installManualAndroid'
  if (platform === 'ios') return 'pwa.iosHintBody'
  return ''
}

export function usePwaInstall() {
  const authStore = useAuthStore()
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
  const showInstall = ref(false)
  const showIosHint = ref(false)
  const installed = ref(isStandaloneDisplay())
  const platform = ref<PwaPlatform>(detectPwaPlatform())
  let engaged = false
  let engagementTimer: ReturnType<typeof setTimeout> | null = null
  let displayModeQuery: MediaQueryList | null = null

  const canPrompt = computed(() => deferredPrompt.value !== null)
  const hintKey = computed(() => manualInstallHintKey(platform.value))

  function dismissed(): boolean {
    return localStorage.getItem(DISMISS_KEY) === '1'
  }

  function maybeReveal() {
    if (!engaged || dismissed() || installed.value || !authStore.isAuthenticated) {
      return
    }
    if (deferredPrompt.value) {
      showInstall.value = true
      showIosHint.value = false
      return
    }
    if (isIosDevice()) {
      showIosHint.value = true
    }
  }

  function onBeforeInstallPrompt(event: Event) {
    event.preventDefault()
    deferredPrompt.value = event as BeforeInstallPromptEvent
    maybeReveal()
  }

  function onAppInstalled() {
    installed.value = true
    deferredPrompt.value = null
    showInstall.value = false
    showIosHint.value = false
    localStorage.removeItem(DISMISS_KEY)
  }

  function onDisplayModeChange(event: MediaQueryListEvent) {
    if (event.matches) {
      installed.value = true
      showInstall.value = false
      showIosHint.value = false
    }
  }

  async function install(): Promise<void> {
    const promptEvent = deferredPrompt.value
    if (!promptEvent) {
      return
    }
    await promptEvent.prompt()
    const choice = await promptEvent.userChoice
    deferredPrompt.value = null
    showInstall.value = false
    if (choice.outcome === 'accepted') {
      installed.value = true
      localStorage.removeItem(DISMISS_KEY)
    }
  }

  function dismiss() {
    showInstall.value = false
    showIosHint.value = false
    localStorage.setItem(DISMISS_KEY, '1')
  }

  watch(() => authStore.isAuthenticated, (authenticated) => {
    if (authenticated) {
      maybeReveal()
    } else {
      showInstall.value = false
      showIosHint.value = false
    }
  })

  onMounted(() => {
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    if (typeof window.matchMedia === 'function') {
      displayModeQuery = window.matchMedia('(display-mode: standalone)')
      displayModeQuery.addEventListener?.('change', onDisplayModeChange)
    }
    engagementTimer = setTimeout(() => {
      engaged = true
      maybeReveal()
    }, ENGAGEMENT_MS)
  })

  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.removeEventListener('appinstalled', onAppInstalled)
    displayModeQuery?.removeEventListener?.('change', onDisplayModeChange)
    if (engagementTimer) {
      clearTimeout(engagementTimer)
    }
  })

  return {
    showInstall,
    showIosHint,
    installed,
    platform,
    canPrompt,
    hintKey,
    install,
    dismiss,
  }
}
